import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminFetchPaymentConfig,
  AdminSavePayoutSettings,
  AdminSaveVendorFees,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  PaymentConfig,
  SavePayoutSettingsPayload,
  SaveVendorFeesPayload,
} from 'src/interfaces/payment'
import { getPaymentConfigPartnerId } from 'src/selectors/paymentConfig'
import { parsePaymentConfigResponse } from 'src/utils/paymentConfig'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import { callSecureApi } from './api/callApi'

interface FetchPaymentConfigPayload {
  partnerId?: string
}

function customerIdParams(partnerId: string) {
  // The LEADING COLON is required. `callSecureApi` substitutes with a plain
  // `endpoint.replace(param.field, param.value)` — a substring replace, not placeholder-aware —
  // so `field: 'customerId'` matches inside `:customerId` and leaves the colon stranded,
  // producing `/v1/admin/partners/:<id>/buyer`. Every other saga in this app spells it `:xxxId`.
  return [{ field: ':customerId', value: partnerId }]
}

/**
 * Chains the post-save refetch, but only while this customer is still the selected one.
 *
 * A save that resolves after the admin has switched customers would otherwise refetch the
 * PREVIOUS customer: `AdminFetchPaymentConfig.request` writes `state.partnerId` back to the old
 * id, the page's `isReady = loaded && loadedPartnerId === customerId` never becomes true for the
 * customer actually on screen, and the tab sticks on "Loading payment configuration…".
 *
 * Skipping the refetch leaves the old customer's slice stale, which is harmless — the page's own
 * `useEffect` refetches whenever the route's `customerId` changes back to it.
 */
export function* refetchIfStillSelected(partnerId: string) {
  const selectedPartnerId: string | null = yield select(getPaymentConfigPartnerId)

  if (selectedPartnerId !== partnerId) {
    return
  }

  yield put(AdminFetchPaymentConfig.request({ partnerId }))
}

export function* onFetchPaymentConfig(action: AsyncAction<FetchPaymentConfigPayload>) {
  const partnerId = action.payload?.partnerId

  if (!partnerId) {
    yield put(AdminFetchPaymentConfig.failed(new Error('Missing partnerId')))

    if (action.next) {
      action.next('Missing partnerId')
    }

    return
  }

  try {
    // `returnResponse: true` reads the body as text instead of `response.json()`. A customer
    // with no config row gets a 200 with an empty body, which `.json()` would reject with a
    // SyntaxError — see parsePaymentConfigResponse.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchCustomerPaymentConfig,
      {
        method: 'GET',
        params: customerIdParams(partnerId),
        returnResponse: true,
      },
    )

    const config: PaymentConfig | null = parsePaymentConfigResponse(payload)

    yield put(AdminFetchPaymentConfig.success({ partnerId, config }))

    if (action.next) {
      action.next(null, config)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.paymentConfig.onFetchPaymentConfig] error' })

    yield put(AdminFetchPaymentConfig.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load payment configuration'))
    }
  }
}

export function* onSavePayoutSettings(action: AsyncAction<SavePayoutSettingsPayload>) {
  const { partnerId, ...body } = action.payload

  try {
    // Only this card's eight scalars. `PartnerPaymentConfigService.update` is a partial save, so
    // omitting `invoiceBreakdowns` leaves the vendor fees untouched — that is what makes two
    // independent saves on one endpoint safe. `partnerId` is destructured out above because it
    // is a real column on the entity and the backend's ValidationPipe has no `whitelist`.
    yield call(callSecureApi, Endpoints.AdminUpdateCustomerPaymentConfig, {
      method: 'PUT',
      params: customerIdParams(partnerId),
      body,
    })

    // The PUT echo is partial — `save({ id, ...data })` returns what it was handed, with none of
    // the vendor-fee state. Refetch rather than store a half-row.
    yield call(refetchIfStillSelected, partnerId)
    yield put(AdminSavePayoutSettings.success(undefined))

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.paymentConfig.onSavePayoutSettings] error' })

    yield put(AdminSavePayoutSettings.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to save payout settings'))
    }
  }
}

export function* onSaveVendorFees(action: AsyncAction<SaveVendorFeesPayload>) {
  const { partnerId, invoiceBreakdowns } = action.payload

  try {
    // Only `invoiceBreakdowns`. See the note in onSavePayoutSettings.
    yield call(callSecureApi, Endpoints.AdminUpdateCustomerPaymentConfig, {
      method: 'PUT',
      params: customerIdParams(partnerId),
      body: { invoiceBreakdowns },
    })

    yield call(refetchIfStillSelected, partnerId)
    yield put(AdminSaveVendorFees.success(undefined))

    if (action.next) {
      action.next(null, true)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.paymentConfig.onSaveVendorFees] error' })

    yield put(AdminSaveVendorFees.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to save vendor fees'))
    }
  }
}

export function* paymentConfigSaga() {
  // Tab and customer switches re-trigger the fetch, so only the newest run matters.
  yield takeLatest(AdminFetchPaymentConfig.type.REQUEST, onFetchPaymentConfig)
  // Every save must run.
  yield takeEvery(AdminSavePayoutSettings.type.REQUEST, onSavePayoutSettings)
  yield takeEvery(AdminSaveVendorFees.type.REQUEST, onSaveVendorFees)
}
